import { Button, Grid, Typography } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { createEvacuation, fetchEvacuations } from '../api/mobileapi';
import { GlobalError } from '../components/GlobalError/GlobalError';
import { useHomeData } from '../components/LoadHomeData/HomeDataProvider';
import { LoadingSpinner } from '../components/LoadingSpinner/LoadingSpinner';

import { useNavigate } from 'react-router-dom';
import { Severity, useToast } from '../components/ToastProvider/ToastProvider';
import { useTranslations } from '../components/Translations/TranslationsProvider';
import { ROUTES } from '../routes';
import { AdamsError, getErrorMessage } from '../utils/errors';
import { FIRST_PAGE_INDEX } from '../utils/utils';

export const EvacuationsPage: React.FC = () => {
    const { translations } = useTranslations();
    const { currentLanguage } = useHomeData();

    const navigate = useNavigate();
    const toast = useToast();

    const {
        data: eventsData,
        error: eventsDataError,
        isLoading,
        refetch: refetchEvacuations,
    } = useQuery({
        queryKey: ['fetchEvacuations', currentLanguage],
        queryFn: () => fetchEvacuations({ language: currentLanguage, page: FIRST_PAGE_INDEX }),
        refetchInterval: 5000,
    });

    useEffect(() => {
        if (!eventsData?.events?.length) return;
        const firstEvent = eventsData.events[0];
        navigate(ROUTES.evacuations.view.replace(':eventId', `${firstEvent.eventID}`));
    }, [eventsData, navigate]);

    const { mutate: createEvent, isLoading: isCreating } = useMutation(createEvacuation, {
        onSuccess: async () => {
            await refetchEvacuations();
            toast(translations.EVACUATIONS_CREATED, Severity.success);
        },
        onError: (err: AdamsError) => {
            const message = getErrorMessage(err);
            if (message) toast(message, Severity.error);
        },
    });

    const handleCreateEvent = () => {
        createEvent({ language: currentLanguage });
    };

    if (eventsDataError) {
        return <GlobalError error={eventsDataError as AdamsError} translations={translations} />;
    }

    return (
        <Card>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', minHeight: '90vh' }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Typography variant="h5" gutterBottom>
                        {translations.EVACUATIONS_TITLE}
                    </Typography>
                    <Button
                        data-testid="event-create-button"
                        style={{ marginTop: '20px', marginBottom: '20px' }}
                        variant="contained"
                        color="primary"
                        onClick={handleCreateEvent}
                        disabled={isCreating}
                    >
                        {translations.EVACUATIONS_CREATE}
                    </Button>

                    <Typography variant="h5" style={{ marginTop: '20px' }}>
                        {translations.EVACUATIONS_SUBTITLE}
                    </Typography>
                    {isLoading ? (
                        <LoadingSpinner />
                    ) : (
                        <Grid
                            mt={4}
                            container
                            direction="column"
                            alignItems="center"
                            justifyContent="center"
                            spacing={0}
                        >
                            <Typography variant="h5" color="textSecondary" style={{ textAlign: 'center' }}>
                                {translations.EVACUATIONS_EMPTY_LIST}
                            </Typography>
                        </Grid>
                    )}
                </LocalizationProvider>
            </CardContent>
        </Card>
    );
};
